import React, { useMemo, useState } from 'react';
import { Select, Input, Pagination, Spin, message, Button, Modal } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useGetCSAList, useGetCSAFilters } from '../../hooks/useOrders';
import { useListingCompanies } from '../../hooks/useCompanies';
import CommonTable from '../../commonComponents/Table/table';
import stc from 'string-to-color';
import './style.scss';
import CSAFilterModalContent from './CSAFilterModalContent';
import CSAFilteredProductsTable from './CSAFilteredProductsTable';

const CSAList = () => {
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [companyCode, setCompanyCode] = useState('');
    const [barcode, setBarcode] = useState('');
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [csaOsIds, setCsaOsIds] = useState([]);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filterList, setFilterList] = useState([]);
    const [draftFilters, setDraftFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const onSuccessGetCSAList = res => {
        const dataList = res?.data ?? [];
        const count = res?.totalCount ?? 0;
        setList(Array.isArray(dataList) ? dataList : []);
        setTotal(typeof count === 'number' ? count : 0);
    
        const osIds = Array.isArray(dataList)
            ? Array.from(
                  new Set(
                      dataList
                          .map(item => item?.osId)
                          .filter(id => typeof id === 'string' && id),
                  ),
              )
            : [];
    
        // Only update osIds when we actually received some
        if (osIds.length > 0) {
            setCsaOsIds(osIds);
        }
    };
    const onErrorGetCSAList = err => {
        message.error(err?.response?.data?.message ?? 'Failed to load CSA list');
    };

    const { data: companyCodeList } = useListingCompanies();

    const filtersForPayload = useMemo(() => {
        const result = {};

        if (!Array.isArray(filterList) || !appliedFilters) {
            return result;
        }

        Object.entries(appliedFilters).forEach(([key, values]) => {
            if (!Array.isArray(values) || values.length === 0) {
                return;
            }

            const schema = filterList.find(f => f.key === key);
            const payloadKey =
                schema && schema.path ? `${schema.path}.${schema.key}` : key;

            result[payloadKey] = values;
        });

        if (barcode) {
            result['csvData.barcode'] = [barcode];
        }

        return result;
    }, [filterList, appliedFilters, barcode]);

    const csaListPayload = useMemo(
        () => ({
            companyCode: companyCode || undefined,
            osids: csaOsIds,
            filters: filtersForPayload,
            pageNum,
            pageSize,
        }),
        [companyCode, csaOsIds, filtersForPayload, pageNum, pageSize],
    );

    const csaFiltersPayload = useMemo(
        () => ({
            osids: csaOsIds,
        }),
        [csaOsIds]
    );

    const onSuccessGetCSAFilters = res => {
        if (res?.status === 200) {
            const raw =
                res?.data?.data && Array.isArray(res.data.data)
                    ? res.data.data
                    : Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res)
                    ? res
                    : [];
            setFilterList(raw);
        }
    };
    const onErrorGetCSAFilters = err => {
        message.error(err?.response?.data?.message ?? 'Failed to load CSA filters');
    };

    useGetCSAFilters(csaFiltersPayload, onSuccessGetCSAFilters, onErrorGetCSAFilters);

    const { isLoading, refetch: refetchCSAList } = useGetCSAList(
        csaListPayload,
        onSuccessGetCSAList,
        onErrorGetCSAList,
    );

    const spinIcon = (
        <div className="csa-list-spin-center">
            <Spin className="csa-list-spin" size="large" />
        </div>
    );

    const columns = [
        {
            title: 'Batch Number',
            dataIndex: 'batchNo',
            key: 'batchNo',
            render: val => val ?? '—',
        },
        {
            title: 'Order Name',
            dataIndex: 'name',
            key: 'name',
            render: val => val ?? '—',
        },
        {
            title: 'Company Code',
            dataIndex: 'companyCode',
            key: 'companyCode',
            render: val => val ?? '—',
        },
        {
            title: 'Client Supporting Article',
            dataIndex: 'isClientSupportingArticle',
            key: 'isClientSupportingArticle',
            render: val => (val === true ? 'Yes' : val === false ? 'No' : '—'),
        },
        // {
        //     title: 'OS ID',
        //     dataIndex: 'osId',
        //     key: 'osId',
        //     render: (val) => val ?? '—',
        // },
    ];

    const showTable = !isLoading && list?.length > 0;
    const showEmpty = !isLoading && (!list || list.length === 0);
    const showPagination = !isLoading && (list?.length > 0 || total > 0);

    const isProductList =
        list?.length > 0 &&
        list[0] &&
        (list[0].articleName != null ||
            (Array.isArray(list[0].barcodes) && list[0].barcodes.length) ||
            list[0].inbound != null);

    const handleClearFilters = () => {
        setAppliedFilters({});
        setPageNum(1);
    };

    return (
        <div className="csa-list">
            <div className="csa-list-header">
                <div className="csa-list-filters">
                    {isProductList ? (
                        <Button
                            type="default"
                            icon={<ClearOutlined />}
                            onClick={handleClearFilters}
                        >
                            Clear filters
                        </Button>
                    ) : (
                        <Select
                            showSearch
                            placeholder="Select company"
                            className="csa-list-company-select"
                            value={companyCode ?? ''}
                            onChange={v => {
                                setCompanyCode(v ?? '');
                                setPageNum(1);
                            }}
                            allowClear
                        >
                            <Select.Option key="all" value="">
                                All
                            </Select.Option>
                            {companyCodeList?.map(el => (
                                <Select.Option key={el._id} value={el.code}>
                                    <div className="csa-list-company-option">
                                        <span className="csa-list-company-color" style={{ backgroundColor: stc(el.code) }} />
                                        <span>{el.code}</span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                    {/* <Input
                        placeholder="Search by barcode"
                        value={barcode}
                        onChange={e => {
                            setBarcode(e.target.value);
                            setPageNum(1);
                        }}
                        allowClear
                        className="csa-list-search-input"
                    /> */}
                </div>
            </div>

            <div className="csa-list-body">
                {isProductList ? (
                    <CSAFilteredProductsTable
                        data={list}
                        loading={isLoading}
                        total={total}
                        pageNum={pageNum}
                        pageSize={pageSize}
                        onPageChange={(page, size) => {
                            setPageNum(page);
                            setPageSize(size || pageSize);
                        }}
                        onRefetch={refetchCSAList}
                        companyCode={companyCode}
                    />
                ) : (
                    <>
                        {isLoading && list.length === 0 && (
                            <div className="csa-list-spin-center">
                                <Spin size="large" />
                            </div>
                        )}
                        {showEmpty && <div className="csa-list-empty">No CSA items found</div>}
                        {showTable && (
                            <div className="csa-list-table-wrap">
                                <CommonTable
                                    columns={columns}
                                    data={list}
                                    rowKey={record => record?._id ?? record?.id ?? record?.barcode}
                                    loading={{
                                        spinning: isLoading,
                                        indicator: spinIcon,
                                    }}
                                    pagination={false}
                                    bordered
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {!isProductList && (
                <div className="csa-list-footer">
                    {showPagination && (
                        <div className="csa-list-pagination">
                            <Pagination
                                current={pageNum}
                                pageSize={pageSize}
                                total={total}
                                showSizeChanger
                                onChange={(page, size) => {
                                    setPageNum(page);
                                    setPageSize(size || pageSize);
                                }}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="csa-list-float-filter">
                <Button
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={<FilterOutlined />}
                    onClick={() => {
                        setDraftFilters(appliedFilters || {});
                        setIsFilterModalOpen(true);
                    }}
                />
            </div>

            <Modal
                title="Filters"
                open={isFilterModalOpen}
                onCancel={() => {
                    setDraftFilters(appliedFilters || {});
                    setIsFilterModalOpen(false);
                }}
                destroyOnClose
                width={'90%'}
                style={{ height: '80dvh' }}
                bodyStyle={{ maxHeight: 'calc(80dvh - 110px)', overflowY: 'auto' }}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            icon={<ClearOutlined />}
                            onClick={() => setDraftFilters({})}
                        >
                            Clear filters
                        </Button>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                                onClick={() => {
                                    setDraftFilters(appliedFilters || {});
                                    setIsFilterModalOpen(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setAppliedFilters(draftFilters || {});
                                    setPageNum(1);
                                    setIsFilterModalOpen(false);
                                }}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                }
            >
                <CSAFilterModalContent
                    filterList={filterList}
                    filters={draftFilters}
                    onChangeFilters={next => {
                        setDraftFilters(next || {});
                    }}
                />
            </Modal>
        </div>
    );
};

export default CSAList;
